import classNames from "classnames";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import Modal from "../../../../../../components/common/Modal";
import PrimaryButton from "../../../../../../components/common/PrimaryButton";
import OutlinedButton from "../../../../../../components/common/OutlinedButton";
import { useEffect, useState } from "react";

interface IClientCredentialsModalProps {
  handleClose: () => void;
  modalOpen: boolean;
  clientId: string;
  clientSecret: string;
}

/**
 * Shows a newly created API client's credentials exactly once (design doc §9 Phase 2) —
 * clientSecret is never retrievable again after this, so closing this modal is the customer's
 * last chance to copy it.
 */
const ClientCredentialsModal = ({
  handleClose,
  modalOpen,
  clientId,
  clientSecret,
}: IClientCredentialsModalProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!modalOpen) {
      setCopied(false);
    }
  }, [modalOpen]);

  const onConfirm = () => {
    const textToCopy = `client_id: ${clientId}\nclient_secret: ${clientSecret}`;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopied(true);
      })
      .catch((error) => {
        console.error("Failed to copy text: ", error);
      });
  };

  const onClose = () => {
    setCopied(false);
    handleClose();
  };

  return (
    <Modal
      visible={modalOpen}
      onCancel={handleClose}
      customModalClassName={styles.modalContainer}>
      <div className={classNames(styles.container)}>
        <img src="/assets/images/svg/Success_tick.svg" alt="success" />

        <div className={styles.headerContainer}>
          <h1 className={styles.title}>{t("lbl_client_created_success")}</h1>
          <p className={styles.description}>
            {t("lbl_client_created_description")}
          </p>
        </div>

        <div className={styles.dataContainer}>
          <div className={styles.dataContentContainer}>
            <img src="/assets/images/svg/email.svg" alt="client id" />
            <div className={styles.dataContent}>
              <p className={styles.dataTitle}>{t("lbl_client_id")}</p>
              <p
                className={styles.dataDescription}
                style={{ direction: "ltr" }}>
                {clientId}
              </p>
            </div>
          </div>
          <div className={styles.divider}></div>
          <div className={styles.dataContentContainer}>
            <img
              src="/assets/images/svg/success_lock.svg"
              alt="client secret"
            />
            <div className={styles.dataContent}>
              <p className={styles.dataTitle}>{t("lbl_client_secret")}</p>
              <p
                className={styles.dataDescription}
                style={{ direction: "ltr" }}>
                {clientSecret}
              </p>
            </div>
          </div>
        </div>

        <PrimaryButton
          onClick={onConfirm}
          style={{
            width: "100%",
            height: "48px",
            borderRadius: "8px",
            textAlign: "center",
            whiteSpace: "nowrap",
            fontSize: "1rem",
            lineHeight: "100%",
            fontWeight: 600,
            alignSelf: "flex-end",
            marginTop: "60px",
            background: copied ? "#3ECC85" : "#0D44AD",
          }}>
          <img
            src="/assets/images/svg/success_copy.svg"
            alt="copy"
            className={styles.copyIcon}
          />
          {copied ? t("lbl_copied") : t("copy_information")}
        </PrimaryButton>
        <OutlinedButton
          onClick={onClose}
          style={{
            width: "100%",
            color: "#0C4BC4",
            background: "#0C4BC41A",
            border: "none",
            fontSize: "1rem",
            fontWeight: 400,
            height: "48px",
            marginTop: "15px",
          }}>
          {t("close")}
        </OutlinedButton>
      </div>
    </Modal>
  );
};

export default ClientCredentialsModal;
